import React from 'react';
import classNames from 'classnames';
import { Empty } from '@vyaguta/vyaguta-ui';
import { useEffect, useMemo, useState } from 'react';

import Loading from 'components/common/loading/Loading';
import ProjectAllocationList from './ProjectAllocationList';
import AllProjectAllocations from './AllProjectAllocations';

import * as permissions from 'constants/permissions';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/page';
import { useUserPermissions } from 'hooks/query/useUserInfo';

import { pluralize } from 'utils/string';
import { getUniqueElements } from 'utils/array';
import { handleError } from 'utils/errorHandler';
import { getFormattedDate, getMonthsAgoDate, isDateInFuture, isDateToday } from 'utils/date';

import { fetchUserAllocations } from 'services/user';
import { AllocationItem } from 'interface/allocation';
import { EmployeeLike } from 'interface/userUtils';

interface ProjectAllocationsProps {
  limit?: number;
  employeeId: number | string;
  authUserId: number | string;
  employeeName: string;
  employee: EmployeeLike & {
    teamManager?: { id?: number | string };
    empStatusHistory?: Array<{ transitionDate?: string }>;
    joinDate?: string;
  };
  isSelf: boolean;
  setAppraiserIds: React.Dispatch<React.SetStateAction<Array<number | string | undefined>>>;
}

const ProjectAllocations: React.FC<ProjectAllocationsProps> = (props) => {
  const { limit = 3, employeeId, authUserId, employeeName, employee, isSelf, setAppraiserIds } = props;
  const [isLoading, setIsLoading] = useState(false);
  const userPermissions = useUserPermissions() as Record<string, boolean>;
  const endDate = getFormattedDate();
  const startDate = getMonthsAgoDate(endDate, 12);
  const [allocationData, setAllocationData] = useState<AllocationItem[]>([]);

  const isTeamManager = useMemo(() => {
    return employee.teamManager?.id === authUserId;
  }, [employee.teamManager?.id, authUserId]);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      try {
        setIsLoading(true);

        const params = {
          startDate,
          endDate,
          userId: employeeId,
          page: DEFAULT_PAGE,
          size: DEFAULT_PAGE_SIZE,
          sortBy: endDate ? 'endDate' : 'potentialEndDate',
          order: 'desc',
        };

        const { data, meta } = (await fetchUserAllocations(params)) as {
          data: AllocationItem[];
          meta: { page: number; pageSize: number; total: number };
        };

        const { page, pageSize, total } = meta;
        const totalPages = Math.ceil(total / pageSize);
        const fetchAllPromise = [];

        for (let i = page + 1; i <= totalPages; i++) {
          fetchAllPromise.push(
            fetchUserAllocations({
              startDate,
              endDate,
              userId: employeeId,
              page: i,
              size: DEFAULT_PAGE_SIZE,
              sortBy: endDate ? 'endDate' : 'potentialEndDate',
              order: 'desc',
            })
          );
        }

        const remainingAllocationList = (await Promise.all(fetchAllPromise)) as Array<{ data: AllocationItem[] }>;
        const formattedAllocationList = remainingAllocationList.reduce(
          (acc: AllocationItem[], { data }: { data: AllocationItem[] }) => [...acc, ...data],
          []
        );

        if (isCancelled) {
          return;
        }

        setAllocationData([...data, ...formattedAllocationList]);
      } catch (e) {
        if (!isCancelled) {
          handleError(e);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [employeeId, endDate, startDate]);

  useEffect(() => {
    const currentAllocations = allocationData.filter((allocation: AllocationItem) =>
      allocation.endDate ? isDateToday(allocation.endDate) || isDateInFuture(allocation.endDate) : true
    );

    const appraiserIds = currentAllocations.map(({ appraiser }) => appraiser?.id);

    const uniqueAppraiserIds = getUniqueElements(appraiserIds);

    setAppraiserIds(uniqueAppraiserIds);
  }, [allocationData, setAppraiserIds]);

  const projectAllocations = useMemo(
    () =>
      allocationData.filter((allocation: AllocationItem) =>
        allocation.endDate ? isDateToday(allocation.endDate) || isDateInFuture(allocation.endDate) : true
      ),
    [allocationData]
  );
  const [hasExpandedAllocations, setHasExpandedAllocations] = useState(false);
  const areAllocationsHidden = projectAllocations.length > limit && !hasExpandedAllocations;

  const sortedProjectAllocations = projectAllocations.sort((a, b) => {
    const dateA = a.endDate || a.potentialEndDate;
    const dateB = b.endDate || b.potentialEndDate;

    return new Date(dateB || '').getTime() - new Date(dateA || '').getTime();
  });

  return isLoading ? (
    <div className="full-scope-card emp-info-container mt-25">
      <Loading />
    </div>
  ) : (
    allocationData.length > 0 && (
      <div className="full-scope-card emp-info-container mt-25">
        <div className="font-bold emp-info-title">{pluralize('Allocation', projectAllocations.length)}</div>
        {sortedProjectAllocations.length > 0 ? (
          <>
            <ul className="projects">
              {sortedProjectAllocations.map((projectAllocation, index) => (
                <ProjectAllocationList
                  key={projectAllocation.id}
                  projectAllocation={projectAllocation}
                  className={classNames('project project-current', {
                    'project-hidden': areAllocationsHidden && index >= limit,
                    'project-prior-to-hidden': areAllocationsHidden && index === limit - 1,
                  })}
                  isSelf={isSelf}
                  isTeamManager={isTeamManager}
                />
              ))}
            </ul>
            {areAllocationsHidden && projectAllocations.length > limit && (
              <button className="project-view-more-button" onClick={() => setHasExpandedAllocations(true)}>
                View More
              </button>
            )}
          </>
        ) : (
          <Empty message="No current allocation." />
        )}

        {(userPermissions[permissions.EMPLOYEES.PROJECTS.VIEW_HISTORY] || authUserId === employeeId) &&
          (!areAllocationsHidden || !projectAllocations.length) && (
            <AllProjectAllocations
              hasCurrentAllocations={projectAllocations.length > 0}
              endDate={endDate}
              employeeId={employeeId}
              employeeName={employeeName}
              startDate={
                employee.empStatusHistory?.[employee.empStatusHistory.length - 1].transitionDate || employee.joinDate
              }
              isSelf={isSelf}
            />
          )}
      </div>
    )
  );
};

export default ProjectAllocations;
